import { createContext, useContext, useState, type ReactNode } from 'react'
import { startOfWeek } from 'date-fns'
import { useSettings } from './settings-context'

interface InsightsWeekContextType {
  currentWeekStart: Date
  setCurrentWeekStart: (date: Date) => void
  goToPreviousWeek: () => void
  goToNextWeek: () => void
}

const InsightsWeekContext = createContext<InsightsWeekContextType | undefined>(undefined)

interface InsightsWeekProviderProps {
  children: ReactNode
}

export function InsightsWeekProvider({ children }: InsightsWeekProviderProps) {
  const { weekStartsOn } = useSettings()
  const weekStartsOnNumber = weekStartsOn === 'monday' ? 1 : 0
  
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: weekStartsOnNumber })
  )

  const goToPreviousWeek = () => {
    const previousWeek = new Date(currentWeekStart)
    previousWeek.setDate(previousWeek.getDate() - 7)
    setCurrentWeekStart(startOfWeek(previousWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStart)
    nextWeek.setDate(nextWeek.getDate() + 7)
    setCurrentWeekStart(startOfWeek(nextWeek, { weekStartsOn: weekStartsOnNumber }))
  }

  const value: InsightsWeekContextType = {
    currentWeekStart,
    setCurrentWeekStart,
    goToPreviousWeek,
    goToNextWeek,
  }

  return (
    <InsightsWeekContext.Provider value={value}>
      {children}
    </InsightsWeekContext.Provider>
  )
}

export function useInsightsWeek() {
  const context = useContext(InsightsWeekContext)
  if (context === undefined) {
    throw new Error('useInsightsWeek must be used within an InsightsWeekProvider')
  }
  return context
}
